import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { Label } from '@components/atoms/Label';
import {
  TileCalculatorInputs,
  TileCalculatorResults,
  OnAddToMaterialCallback,
  OnAddAllMaterialsCallback,
  MaterialFromCalculator,
} from '../../types/calculator.types';
import { Plus } from 'lucide-react';
import { useTranslation } from '@hooks/useTranslation';

interface TileCalculatorProps {
  onAddToMaterial?: OnAddToMaterialCallback;
  onAddAllMaterials?: OnAddAllMaterialsCallback;
}

const TileCalculator: React.FC<TileCalculatorProps> = ({
  onAddToMaterial,
  onAddAllMaterials,
}) => {
  const tCalc = useTranslation('calculators');
  const [inputs, setInputs] = useState<TileCalculatorInputs>({
    roomLength: '',
    roomWidth: '',
    tileLength: '',
    tileWidth: '',
    groutJoint: '',
    wastePercentage: 10,
    tilesPerBox: '',
  });

  const [results, setResults] = useState<TileCalculatorResults | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Scroll to results when they appear
  useEffect(() => {
    if (results && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [results]);

  const handleInputChange =
    (field: keyof TileCalculatorInputs) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setInputs((prev) => ({
        ...prev,
        [field]: field === 'wastePercentage' ? Number(value) : value,
      }));
    };

  const handleCalculate = () => {
    setResults(null);

    const parsedRoomLength = parseFloat(inputs.roomLength);
    const parsedRoomWidth = parseFloat(inputs.roomWidth);
    const parsedTileLength = parseFloat(inputs.tileLength);
    const parsedTileWidth = parseFloat(inputs.tileWidth);
    const parsedGroutJoint = parseFloat(inputs.groutJoint);
    const parsedWastePercentage = parseFloat(inputs.wastePercentage.toString());
    const parsedTilesPerBox = parseFloat(inputs.tilesPerBox);

    if (
      isNaN(parsedRoomLength) ||
      isNaN(parsedRoomWidth) ||
      isNaN(parsedTileLength) ||
      isNaN(parsedTileWidth) ||
      isNaN(parsedGroutJoint) ||
      isNaN(parsedWastePercentage) ||
      isNaN(parsedTilesPerBox) ||
      parsedRoomLength <= 0 ||
      parsedRoomWidth <= 0 ||
      parsedTileLength <= 0 ||
      parsedTileWidth <= 0 ||
      parsedGroutJoint < 0 ||
      parsedWastePercentage < 0 ||
      parsedTilesPerBox <= 0
    ) {
      return;
    }

    try {
      const tileLengthInFeet = parsedTileLength / 12;
      const tileWidthInFeet = parsedTileWidth / 12;
      const groutJointInFeet = parsedGroutJoint / 12;

      const numTilesLength = Math.ceil(
        parsedRoomLength / (tileLengthInFeet + groutJointInFeet),
      );
      const numTilesWidth = Math.ceil(
        parsedRoomWidth / (tileWidthInFeet + groutJointInFeet),
      );

      const tilesWithoutWaste = numTilesLength * numTilesWidth;
      const wasteFactor = 1 + parsedWastePercentage / 100;
      const calculatedTilesNeeded = Math.ceil(tilesWithoutWaste * wasteFactor);

      const calculatedRoomArea = parsedRoomLength * parsedRoomWidth;
      const groutFactor = 0.1;
      const calculatedGroutNeeded =
        calculatedRoomArea * groutFactor * (parsedGroutJoint / 0.125);

      const calculatedBoxesNeeded = Math.ceil(
        calculatedTilesNeeded / parsedTilesPerBox,
      );

      setResults({
        roomArea: calculatedRoomArea.toFixed(2),
        tilesNeeded: calculatedTilesNeeded,
        groutNeeded: calculatedGroutNeeded.toFixed(1),
        boxesNeeded: calculatedBoxesNeeded,
      });
    } catch (error) {
      console.error('Calculation failed:', error);
    }
  };

  return (
    <div>
      <h3 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-6">
        {tCalc.names.tile}
      </h3>

      {/* Room Dimensions */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.dimensions.roomDimensionsFt}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label label={tCalc.dimensions.length} htmlFor="roomLength" />
            <Input
              id="roomLength"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 15`}
              value={inputs.roomLength}
              onChange={handleInputChange('roomLength')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.dimensions.width} htmlFor="roomWidth" />
            <Input
              id="roomWidth"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 10`}
              value={inputs.roomWidth}
              onChange={handleInputChange('roomWidth')}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Tile and Grout Dimensions */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.tile.tileAndGroutDimensions}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label label={tCalc.tile.tileLengthIn} htmlFor="tileLength" />
            <Input
              id="tileLength"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 12`}
              value={inputs.tileLength}
              onChange={handleInputChange('tileLength')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.tile.tileWidthIn} htmlFor="tileWidth" />
            <Input
              id="tileWidth"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 12`}
              value={inputs.tileWidth}
              onChange={handleInputChange('tileWidth')}
              className="mt-1"
            />
          </div>
          <div>
            <Label
              label={tCalc.tile.groutJointWidthIn}
              htmlFor="groutJoint"
            />
            <Input
              id="groutJoint"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 0.125 (1/8 inch)`}
              value={inputs.groutJoint}
              onChange={handleInputChange('groutJoint')}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.tile.additionalInformation}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label label={tCalc.dimensions.wasteAllowancePct} htmlFor="wastePercentage" />
            <Input
              id="wastePercentage"
              type="number"
              inputMode="numeric"
              min={0}
              max={100}
              placeholder={`${tCalc.actions.examplePrefix}, 10`}
              value={inputs.wastePercentage}
              onChange={handleInputChange('wastePercentage')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.tile.tilesPerBox} htmlFor="tilesPerBox" />
            <Input
              id="tilesPerBox"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 10`}
              value={inputs.tilesPerBox}
              onChange={handleInputChange('tilesPerBox')}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      <Button
        fullWidth
        variant="primary"
        size="lg"
        onClick={handleCalculate}
        className="mb-6">
        {tCalc.tile.calculateButton}
      </Button>

      {results && (
        <div
          ref={resultsRef}
          className="p-4 mb-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border-t-4 border-indigo-600">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
            {tCalc.actions.results}
          </h4>
          <div className="space-y-3">
            {/* Room Area - Informational only */}
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">{tCalc.dimensions.roomArea}</span>{' '}
              <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                {results.roomArea}
              </span>{' '}
              {tCalc.dimensions.sqFt}
            </p>

            {/* Tiles Needed */}
            <div className="flex items-center justify-between">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{tCalc.tile.tilesNeeded}</span>{' '}
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {results.tilesNeeded}
                </span>{' '}
                {results.tilesNeeded === 1 ? tCalc.tile.tilesUnitOne : tCalc.tile.tilesUnitOther}
              </p>
              {onAddToMaterial && (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() =>
                    onAddToMaterial({
                      amount: results.tilesNeeded,
                      amountType: 'units',
                      amountSystem: 'imperial',
                      suggestedName: tCalc.materials.tile,
                      resultLabel: tCalc.tile.tilesNeeded,
                    })
                  }>
                  {tCalc.actions.addToMaterial}
                </Button>
              )}
            </div>

            {/* Grout Needed */}
            <div className="flex items-center justify-between">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{tCalc.tile.groutNeeded}</span>{' '}
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {results.groutNeeded}
                </span>{' '}
                {tCalc.tile.groutNeededUnit}
              </p>
              {onAddToMaterial && (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() =>
                    onAddToMaterial({
                      amount: parseFloat(results.groutNeeded),
                      amountType: 'pounds',
                      amountSystem: 'imperial',
                      suggestedName: tCalc.materials.grout,
                      resultLabel: tCalc.tile.groutNeeded,
                    })
                  }>
                  {tCalc.actions.addToMaterial}
                </Button>
              )}
            </div>

            {/* Boxes of Tiles */}
            <div className="flex items-center justify-between">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{tCalc.tile.boxesOfTiles}</span>{' '}
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {results.boxesNeeded}
                </span>{' '}
                {results.boxesNeeded === 1 ? tCalc.tile.boxesUnitOne : tCalc.tile.boxesUnitOther}
              </p>
              {onAddToMaterial && (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() =>
                    onAddToMaterial({
                      amount: results.boxesNeeded,
                      amountType: 'boxes',
                      amountSystem: 'imperial',
                      suggestedName: tCalc.tile.tileBoxesMaterial,
                      resultLabel: tCalc.tile.boxesOfTiles,
                    })
                  }>
                  {tCalc.actions.addToMaterial}
                </Button>
              )}
            </div>

            {/* Add All Materials Button */}
            {onAddAllMaterials && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  fullWidth
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => {
                    const allMaterials: MaterialFromCalculator[] = [
                      {
                        amount: results.tilesNeeded,
                        amountType: 'units',
                        amountSystem: 'imperial',
                        suggestedName: tCalc.materials.tile,
                        resultLabel: tCalc.tile.tilesNeeded,
                      },
                      {
                        amount: parseFloat(results.groutNeeded),
                        amountType: 'pounds',
                        amountSystem: 'imperial',
                        suggestedName: tCalc.materials.grout,
                        resultLabel: tCalc.tile.groutNeeded,
                      },
                      {
                        amount: results.boxesNeeded,
                        amountType: 'boxes',
                        amountSystem: 'imperial',
                        suggestedName: tCalc.tile.tileBoxesMaterial,
                        resultLabel: tCalc.tile.boxesOfTiles,
                      },
                    ];
                    onAddAllMaterials(allMaterials);
                  }}>
                  {tCalc.actions.addAllMaterials} (3)
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TileCalculator;
